import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { attachmentsService, type Attachment } from '../services/attachments'
import type { EntityType } from '../services/comments'

interface AttachmentListProps {
  entityType: EntityType
  entityId: number
  /** Show only attachments with this purpose. */
  purpose?: string
  /** Render an upload button. Default true. */
  allowUpload?: boolean
  /** Show "Take photo" button (capture from camera) when uploading. */
  allowCamera?: boolean
  className?: string
}

const formatSize = (b: number): string => {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}

export function AttachmentList({
  entityType,
  entityId,
  purpose,
  allowUpload = true,
  allowCamera = false,
  className = '',
}: AttachmentListProps) {
  const [items, setItems] = useState<Attachment[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const cameraInputRef = useRef<HTMLInputElement | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await attachmentsService.list(entityType, entityId)
      setItems(purpose ? data.filter(a => a.purpose === purpose) : data)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId, purpose])

  useEffect(() => {
    load()
  }, [load])

  const upload = async (file: File) => {
    setUploading(true)
    try {
      await attachmentsService.upload({ entityType, entityId, file, purpose })
      await load()
      toast.success(`Uploaded ${file.name}`)
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = '' // allow re-selecting same file
    if (f) upload(f)
  }

  const open = async (id: number) => {
    try {
      const url = await attachmentsService.downloadUrl(id)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      toast.error('Failed to fetch download URL')
    }
  }

  const remove = async (id: number) => {
    if (!confirm('Delete this attachment?')) return
    try {
      await attachmentsService.remove(id)
      setItems(prev => prev.filter(a => a.id !== id))
    } catch {
      toast.error('Failed to delete')
    }
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {loading ? (
        <p className="text-sm text-[var(--color-text-secondary)]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--color-text-secondary)]">No files attached.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {items.map(a => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2"
            >
              <button
                type="button"
                onClick={() => open(a.id)}
                className="min-w-0 flex-1 truncate text-left text-sm text-[var(--color-accent)] hover:underline"
              >
                {a.file_name}
                {a.version > 1 && (
                  <span className="ml-2 text-xs text-[var(--color-text-secondary)]">
                    v{a.version}
                  </span>
                )}
              </button>
              <span className="hidden text-xs text-[var(--color-text-secondary)] sm:inline">
                {formatSize(a.file_size)}
              </span>
              <button
                type="button"
                onClick={() => remove(a.id)}
                className="min-h-[36px] min-w-[36px] text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-danger)]"
                aria-label="Delete attachment"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {allowUpload && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={onPick}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : '＋ Upload file'}
          </button>
          {allowCamera && (
            <>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onPick}
              />
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
              >
                📷 Take photo
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
